/**
 * @file procurement-params-tab.tsx
 * @module app/modules/setting/procurement-params-tab
 * @description Settings > Manage MOQ / DOIH / Tonnage: download the Excel with every supplier
 *   and its SKUs (current values filled in), upload the filled file, check it (shows every
 *   change, errors and warnings; saves nothing) and save. Values apply to every warehouse.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useRef, useState } from "react";
import { Alert, Button, Card, Spinner, Table } from "react-bootstrap";
import APICallService from "../../../api/apiCallService";
import { PURCHASEORDER } from "../../../api/apiEndPoints";
import { Settings } from "../../../utils/constants";
import { error, success } from "../../../Global/toast";

interface RowMessage {
  sheet?: string;
  row: number;
  message: string;
}
interface Summary {
  rowsRead: number;
  errors: number;
  warnings: number;
  changes: { leadDuration: number; tonnage: number; moq: number; doih: number };
}
interface CheckResult {
  summary: Summary;
  errors: RowMessage[];
  warnings: RowMessage[];
}
interface TemplateResult {
  fileName: string;
  rows: { suppliers: number; moq: number; doih: number };
  file: string;
}

const CHANGE_LABELS: [keyof Summary["changes"], string][] = [
  ["moq", "MOQ (Vendor Profile > Product Catalog)"],
  ["doih", "DOIH (Price Catalog)"],
  ["leadDuration", "Supplier lead duration"],
  ["tonnage", "Supplier PO tonnage"],
];

/** base64 -> downloaded .xlsx file */
const saveBase64 = (b64: string, fileName: string) => {
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

const ProcurementParamsTab = () => {
  const [file, setFile] = useState<File | null>(null);
  const [check, setCheck] = useState<CheckResult | null>(null);
  const [busy, setBusy] = useState<"" | "download" | "check" | "apply">("");
  const input = useRef<HTMLInputElement>(null);

  const post = async (endpoint: string) => {
    const form = new FormData();
    if (file) form.append("file", file);
    return new APICallService(endpoint, form, "", "", "", "", Settings).callAPI();
  };

  const download = async () => {
    setBusy("download");
    const res: TemplateResult | null = await new APICallService(PURCHASEORDER.procurementParamsTemplate, undefined, "", "", "", "", Settings).callAPI();
    setBusy("");
    if (res?.file) {
      saveBase64(res.file, res.fileName);
      success(`Downloaded: ${res.rows.suppliers} suppliers, ${res.rows.moq.toLocaleString("en-US")} MOQ rows, ${res.rows.doih.toLocaleString("en-US")} DOIH rows`);
    }
  };

  const runCheck = async () => {
    if (!file) return;
    setBusy("check");
    const res: CheckResult | null = await post(PURCHASEORDER.procurementParamsCheck);
    setBusy("");
    if (res?.summary) setCheck(res);
  };

  const apply = async () => {
    if (!file || !check || check.summary.errors) return;
    setBusy("apply");
    const res: { saved?: Summary["changes"] } | null = await post(PURCHASEORDER.procurementParamsApply);
    setBusy("");
    if (res?.saved) {
      const s = res.saved;
      success(`Saved: ${s.moq} MOQ, ${s.doih} DOIH, ${s.leadDuration} lead duration, ${s.tonnage} tonnage`);
      setFile(null);
      setCheck(null);
      if (input.current) input.current.value = "";
    } else {
      error("Nothing was saved");
    }
  };

  const total = check ? Object.values(check.summary.changes).reduce((a, b) => a + b, 0) : 0;

  return (
    <Card className="border">
      <Card.Body>
        <h3 className="fs-20 fw-bolder mb-2">Manage MOQ / DOIH / Tonnage</h3>
        <div className="fs-14 text-gray-700 mb-6">
          <p className="mb-2">Download the Excel, fill it in and upload it. Each value is entered once, on its own sheet:</p>
          <ul className="mb-2">
            <li>
              <b>Suppliers</b>: one row per supplier: <b>Lead Duration</b> (days) and <b>PO Tonnage</b> (tons).
            </li>
            <li>
              <b>MOQ</b>: one row per supplier and product: <b>MOQ</b> and its <b>unit</b> (pre-filled with the unit of the last PO).
            </li>
            <li>
              <b>DOIH</b>: one row per product: days of stock to hold.
            </li>
          </ul>
          <p className="mb-0">
            Values apply to every warehouse. Leave a cell empty to keep the current value; type 0 to remove it. A supplier can have
            tonnage, MOQs, both or nothing.
          </p>
        </div>

        <div className="d-flex flex-wrap gap-3 align-items-center mb-6">
          <Button variant="light-primary" onClick={download} disabled={busy !== ""}>
            {busy === "download" ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Download Excel (all suppliers and SKUs)
          </Button>
          <input
            ref={input}
            type="file"
            accept=".xlsx,.xls"
            className="form-control w-auto"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setCheck(null);
            }}
          />
          <Button variant="primary" onClick={runCheck} disabled={!file || busy !== ""}>
            {busy === "check" ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Check file
          </Button>
        </div>

        {check && (
          <>
            <Alert variant={check.summary.errors ? "danger" : total ? "primary" : "secondary"} className="fs-14">
              {check.summary.rowsRead.toLocaleString("en-US")} rows read.{" "}
              {check.summary.errors
                ? `${check.summary.errors} error(s): fix them in the file and check again. Nothing will be saved.`
                : total
                  ? `${total} change(s) will be saved.`
                  : "No changes found."}
              {check.summary.warnings ? ` ${check.summary.warnings} warning(s) (those values are skipped).` : ""}
            </Alert>
            <Table size="sm" bordered className="fs-14 w-auto mb-5">
              <tbody>
                {CHANGE_LABELS.map(([k, label]) => (
                  <tr key={k}>
                    <td className="pe-6">{label}</td>
                    <td className="fw-bold text-end px-4">{check.summary.changes[k]}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {[
              { title: "Errors", list: check.errors, cls: "text-danger" },
              { title: "Warnings", list: check.warnings, cls: "text-warning" },
            ]
              .filter((g) => g.list.length)
              .map((g) => (
                <div key={g.title} className="mb-5">
                  <div className={`fs-15 fw-bold mb-2 ${g.cls}`}>
                    {g.title} ({g.list.length}
                    {g.list.length >= 200 ? "+" : ""})
                  </div>
                  <div className="border rounded p-3" style={{ maxHeight: 240, overflowY: "auto" }}>
                    {g.list.map((m, i) => (
                      <div key={i} className="fs-13">
                        {m.sheet ? `${m.sheet} sheet, ` : ""}row {m.row}: {m.message}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            <Button variant="success" onClick={apply} disabled={busy !== "" || !!check.summary.errors || !total}>
              {busy === "apply" ? <Spinner animation="border" size="sm" className="me-2" /> : null}
              Save {total} change(s)
            </Button>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ProcurementParamsTab;
